import i18next from "i18next";
import {
  AgeRating,
  AgeRatingSystem,
  FeaturedCard,
  Game,
  GameSearchStatus,
} from "@onmo/graphql/graphql";
import { isYouTubeUrl } from "./media";
import { map, uniqBy, upperFirst } from "lodash-es";
import { GameMetadata } from "../types/gamePack";
import { DEFAULT_LANGUAGE } from "./language";
import { getLocalizedField, isText } from "./localizedField";

type GameImages = Record<string, string | undefined>;

const EMPTY_METADATA = {} as GameMetadata;
const metadataCache = new WeakMap<object, GameMetadata>();

export const getGameMetadata = (
  game?: Pick<Game, "metadata"> | null,
): GameMetadata => {
  if (!game?.metadata) return EMPTY_METADATA;
  const cached = metadataCache.get(game);
  if (cached) return cached;
  try {
    const parsed = JSON.parse(game.metadata) as GameMetadata;
    metadataCache.set(game, parsed);
    return parsed;
  } catch {
    metadataCache.set(game, EMPTY_METADATA);
    return EMPTY_METADATA;
  }
};

export const getGameTitle = (
  game?: Pick<Game, "metadata" | "title"> | null,
  language: string = i18next.language || DEFAULT_LANGUAGE,
) =>
  getLocalizedField(getGameMetadata(game), "title", language) ||
  game?.title ||
  "";

export const getGameDescription = (
  game?: Pick<Game, "metadata" | "description"> | null,
  language: string = i18next.language || DEFAULT_LANGUAGE,
) =>
  getLocalizedField(getGameMetadata(game), "description", language) ||
  game?.description ||
  "";

export const getGameWarning = (
  metadata?: GameMetadata | null,
  language: string = i18next.language || DEFAULT_LANGUAGE,
) => getLocalizedField(metadata, "warning", language) || "";

/*
 * Untranslated labels stay in English rather than disappearing from the chips.
 * The map is parsed CMS JSON, so a label named after an Object.prototype member
 * resolves to a function here; only a real string may be trusted.
 */
export const getGameLabels = (
  metadata?: GameMetadata | null,
  language: string = i18next.language || DEFAULT_LANGUAGE,
): string[] => {
  const translated = metadata?.translations?.[language]?.labels;
  return (metadata?.labels ?? []).map((label) => {
    const value = translated?.[label];
    return isText(value) ? value.trim() : label;
  });
};

export const getImageWithFallback = (
  gameImages?: GameImages,
  baseKey?: string,
  newKey?: string,
) => {
  if (!gameImages) return undefined;
  const collect = (key?: string) =>
    key
      ? [gameImages[key], gameImages[`${key}2x`], gameImages[`${key}3x`]]
      : [];

  return [...collect(newKey), ...collect(baseKey)].find(Boolean);
};

export const sortYouTubeFirst = <T extends string | Record<string, unknown>>(
  items: T[],
  getUrl?: (item: T) => string,
): T[] => {
  const resolve = getUrl || ((item: T) => item as string);

  return items
    .reduce<[T[], T[]]>(
      ([yt, other], item) => {
        isYouTubeUrl(resolve(item)) ? yt.push(item) : other.push(item);
        return [yt, other];
      },
      [[], []],
    )
    .flat();
};

export const checkBYOGGame = (game?: Game) =>
  game?.category?.includes("BYOG") ?? false;

export const isEssentialGame = (game?: Game) =>
  game?.category?.includes("PID") ?? false;

export const isNewGame = (game?: Game) => game?.category?.includes("New");

export const getSearchGameTitle = (
  query: string,
  status?: GameSearchStatus,
) => {
  switch (status) {
    case GameSearchStatus.MatchFound:
      return i18next.t("Search_Top Results");
    case GameSearchStatus.MatchFoundNotPlayable:
      return i18next.t(
        "Search_query is unavailable. Try these suggestions instead.",
        {
          query,
        },
      );
    case GameSearchStatus.LabelMatchFound:
      return i18next.t("Search_query Games", { query: upperFirst(query) });
    case GameSearchStatus.NotFound:
      return i18next.t("Search_More to explore");
    default:
      return "";
  }
};

export const getGames = (items?: FeaturedCard[]) =>
  uniqBy(map(items, "data.game"), "id").filter((game) => Boolean(game));

export const getLicenseTag = (price: number, game?: Game): string => {
  if (!game) return "";
  if (isEssentialGame(game)) return i18next.t("GameDetail_Essential Pass");
  if (!checkBYOGGame(game)) return "";
  return price === 0
    ? i18next.t("GameDetail_Free-to-Play")
    : i18next.t("GameDetail_Ownership Required");
};

export const getPegiAge = (ageRatings?: AgeRating[] | null): number => {
  const pegi = ageRatings?.find((r) => r.system === AgeRatingSystem.Pegi);
  return Number(pegi?.rating) || 0;
};

export const getGameRating = (rating: number) => {
  if (!rating) return "N/A";
  const ratingFormat = rating / 10;
  return (Math.round(ratingFormat * 10) / 10).toFixed(1);
};
